import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Header menu — Flora/Horizon with Shrine-style stability for media mega menus.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu link.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    this.addEventListener('pointermove', this.#trackPointer, { passive: true });
    this.addEventListener('pointerenter', this.#cancelDeactivate, true);
    this.addEventListener('pointerleave', this.#scheduleDeactivateFromMenu, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointermove', this.#trackPointer);
    this.removeEventListener('pointerenter', this.#cancelDeactivate, true);
    this.removeEventListener('pointerleave', this.#scheduleDeactivateFromMenu, true);
    this.#clearDeactivateTimer();
    if (this.#state.activeItem) {
      this.#disableSafetyBridge(this.#state.activeItem);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreOpenStateAfterMorph();
  }

  #resizeListener = debounce(() => {
    if (this.#hasMediaMenu && this.ariaExpanded === 'true') return;
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivateTimer;

  /** @type {State} */
  #state = {
    activeItem: null,
  };

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get #hasMediaMenu() {
    return this.classList.contains('header-menu--has-media');
  }

  #clearDeactivateTimer() {
    if (this.#deactivateTimer !== undefined) {
      clearTimeout(this.#deactivateTimer);
      this.#deactivateTimer = undefined;
    }
  }

  /** @type {{ x: number, y: number }} */
  #lastPointer = { x: 0, y: 0 };

  #trackPointer = (event) => {
    if ('clientX' in event) {
      this.#lastPointer.x = event.clientX;
      this.#lastPointer.y = event.clientY;
    }
  };

  #cancelDeactivate = () => {
    this.#clearDeactivateTimer();
  };

  #scheduleDeactivateFromMenu = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!this.contains(event.target) && !this.overflowMenu?.contains(event.target)) return;
    this.#scheduleDeactivate(event);
  };

  /**
   * @param {Event} [event]
   */
  #scheduleDeactivate(event) {
    this.#clearDeactivateTimer();

    const delay = this.#hasMediaMenu ? 120 : 0;

    if (delay === 0) {
      this.#deactivateIfPointerLeft(event);
      return;
    }

    this.#deactivateTimer = setTimeout(() => {
      this.#deactivateIfPointerLeft(event);
    }, delay);
  }

  /**
   * @param {Event} [event]
   */
  #deactivateIfPointerLeft(event) {
    if (this.#pointerIsInsideMenu(event)) return;
    this.#deactivate();
  }

  /**
   * @param {Event} [event]
   * @returns {boolean}
   */
  #pointerIsInsideMenu(event) {
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return true;

    const coords = getPointerCoords(event) ?? this.#lastPointer;
    if (!coords.x && !coords.y) return false;

    const target = document.elementFromPoint(coords.x, coords.y);
    if (!(target instanceof Element)) return false;

    if (this.contains(target)) return true;

    const overflowRoot = this.overflowMenu?.getRootNode();
    if (overflowRoot instanceof ShadowRoot && overflowRoot.host.contains(target)) return true;

    return Boolean(target.closest('.menu-list__submenu, [ref="submenu[]"]'));
  }

  /**
   * Flora safety bridge: invisible ::after from the top link down to the header bottom.
   * Static only (no pointermove toggling) so moving into the submenu does not spuriously close.
   *
   * @param {HTMLElement} item
   */
  #enableSafetyBridge(item) {
    if (!this.#hasMediaMenu) return;

    const menuLink = resolveMenuLink(item) ?? item;
    if (!(menuLink instanceof HTMLElement) || !menuLink.hasAttribute('aria-haspopup')) return;

    const rect = menuLink.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary =
      isOverlap && this.headerComponent
        ? this.headerComponent.querySelector('.header__row--top')
        : this.headerComponent;
    const bridgeBottom = boundary?.getBoundingClientRect().bottom ?? rect.bottom;
    const boxHeight = Math.max(bridgeBottom - rect.top, rect.height);

    menuLink.style.setProperty('--box-height', `${boxHeight}px`);
    menuLink.dataset.safetyBox = 'true';
  }

  /**
   * @param {HTMLElement} item
   */
  #disableSafetyBridge(item) {
    const menuLink = resolveMenuLink(item) ?? item;
    if (!(menuLink instanceof HTMLElement)) return;

    menuLink.style.removeProperty('--box-height');
    delete menuLink.dataset.safetyBox;
  }

  /**
   * @param {number} height
   * @param {HTMLElement | null | undefined} [submenu]
   */
  #syncOpenHeights(height, submenu) {
    if (!this.headerComponent) return;
    if (height > 0 && submenu?.dataset.heightLocked !== undefined) return;

    this.headerComponent.style.setProperty('--submenu-height', `${height}px`);
    this.#setFullOpenHeaderHeight(height);

    if (this.#hasMediaMenu && height > 0) {
      this.headerComponent.setAttribute('data-mega-menu-open', '');
    } else if (height === 0) {
      this.headerComponent.removeAttribute('data-mega-menu-open');
    }
  }

  /**
   * @param {HTMLElement} submenu
   */
  #lockSubmenuHeight(submenu) {
    submenu.dataset.heightLocked = '';
  }

  /**
   * @param {HTMLElement} submenu
   */
  #openSubmenuPanel(submenu) {
    const apply = () => {
      const height = measureSubmenuHeight(submenu, this.#hasMediaMenu);
      if (height <= 0) {
        if (submenu.dataset.heightLocked !== undefined) return true;
        return false;
      }

      this.#syncOpenHeights(height, submenu);
      this.style.setProperty('--submenu-opacity', '1');
      this.#lockSubmenuHeight(submenu);
      return true;
    };

    if (apply()) return;

    requestAnimationFrame(() => {
      apply();
    });
  }

  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    const itemKey = getMenuItemKey(item);
    const activeKey = getMenuItemKey(this.#state.activeItem);
    if (itemKey && itemKey === activeKey) return;

    const isNewItem = itemKey !== activeKey;

    this.#clearDeactivateTimer();
    this.dispatchEvent(new MegaMenuHoverEvent());

    const isDefaultSlot = event.target.slot === '';
    const menuLink = resolveMenuLink(item) ?? item;
    const hasDropdown = menuLink instanceof HTMLElement && menuLink.hasAttribute('aria-haspopup');

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      const previousLink = resolveMenuLink(previouslyActiveItem) ?? previouslyActiveItem;
      if (previousLink instanceof HTMLElement) {
        previousLink.ariaExpanded = 'false';
      }
      this.#disableSafetyBridge(previouslyActiveItem);
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu && previousSubmenu !== findSubmenu(item)) {
        delete previousSubmenu.dataset.active;
        delete previousSubmenu.dataset.heightLocked;
      }
    }

    this.#state.activeItem = item;

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      if (!this.#hasMediaMenu) {
        delete submenu.dataset.heightLocked;
      }
      submenu.dataset.active = '';
      if (menuLink instanceof HTMLElement && hasDropdown) {
        menuLink.ariaExpanded = 'true';
        this.ariaExpanded = 'true';
        this.#enableSafetyBridge(item);
      }
      if (isNewItem) {
        this.#openSubmenuPanel(submenu);
      }
      return;
    }

    this.ariaExpanded = 'false';
    if (menuLink instanceof HTMLElement) {
      menuLink.ariaExpanded = 'false';
    }

    let finalHeight = 0;
    if (!isDefaultSlot) {
      finalHeight = this.#getOverflowListLinksHeight();
    }

    this.#syncOpenHeights(finalHeight, null);
    this.style.setProperty('--submenu-opacity', finalHeight > 0 ? '1' : '0');
  };

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    if (this.#hasMediaMenu) {
      if (this.#pointerIsInsideMenu(event)) return;
      this.#scheduleDeactivate();
      return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const listItem = this.#resolveActiveListItem();
    const isMovingWithinListItem =
      event.relatedTarget instanceof Node && listItem?.contains(event.relatedTarget);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      (event.type === 'blur' || event.type === 'pointerleave') &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinListItem || isMovingToOverflowMenu || isMovingToSubmenu) {
      return;
    }

    this.#deactivate();
  }

  #deactivate = (item = this.#state.activeItem) => {
    this.#clearDeactivateTimer();

    const activeKey = getMenuItemKey(this.#state.activeItem);
    const itemKey = getMenuItemKey(item);
    if (!itemKey || itemKey !== activeKey) return;

    if (this.#pointerIsInsideMenu()) return;
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.#syncOpenHeights(0, null);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);
    const menuLink = resolveMenuLink(item) ?? item;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    if (menuLink instanceof HTMLElement) {
      menuLink.ariaExpanded = 'false';
    }
    this.#disableSafetyBridge(item);

    if (submenu) {
      delete submenu.dataset.active;
      delete submenu.dataset.heightLocked;
    }

    this.headerComponent?.removeAttribute('data-mega-menu-open');
  };

  #restoreOpenStateAfterMorph() {
    const key = getMenuItemKey(this.#state.activeItem);
    if (!key) return;

    const item = findMenuItemByKey(this, key);
    if (!item) return;

    this.#state.activeItem = item;
    const menuLink = resolveMenuLink(item) ?? item;
    if (menuLink instanceof HTMLElement && menuLink.hasAttribute('aria-haspopup')) {
      menuLink.ariaExpanded = 'true';
      this.ariaExpanded = 'true';
      this.#enableSafetyBridge(item);
    }

    const submenu = findSubmenu(item);
    if (!submenu) return;

    submenu.dataset.active = '';
    if (submenu.dataset.heightLocked !== undefined) {
      const height = measureSubmenuHeight(submenu, this.#hasMediaMenu);
      if (height > 0) {
        this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
        this.#setFullOpenHeaderHeight(height);
        this.headerComponent?.setAttribute('data-mega-menu-open', '');
      }
    }

    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #resolveActiveListItem() {
    const item = this.#state.activeItem;
    if (!(item instanceof HTMLElement)) return null;

    if (item.isConnected) {
      return item.closest('.menu-list__list-item');
    }

    const fresh = findMenuItemByKey(this, getMenuItemKey(item));
    if (!fresh) return null;

    this.#state.activeItem = fresh;
    if (fresh.hasAttribute('aria-haspopup')) {
      fresh.ariaExpanded = 'true';
    }
    return fresh.closest('.menu-list__list-item');
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const sm = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (sm) cb(sm);
      });
    };

    mapSubmenus((sm) => {
      sm.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((sm) => {
      sm.style.removeProperty('display');
    });
    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * @param {Event} [event]
 * @returns {{ x: number, y: number } | null}
 */
function getPointerCoords(event) {
  if (event && 'clientX' in event && typeof event.clientX === 'number') {
    return { x: event.clientX, y: event.clientY };
  }

  return null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  const fromDescendant = element.querySelector('[ref="menuitem"]');
  if (fromDescendant instanceof HTMLElement) {
    return fromDescendant;
  }

  return element.closest('[ref="menuitem"]');
}

/**
 * @param {HTMLElement} item
 * @returns {HTMLElement | null}
 */
function resolveMenuLink(item) {
  if (item.matches('.menu-list__link')) {
    return item;
  }

  const link = item.querySelector('.menu-list__link');
  return link instanceof HTMLElement ? link : null;
}

/**
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  if (!(element instanceof Element)) return null;

  const listItem = element.closest('.menu-list__list-item');
  const submenu =
    listItem?.querySelector(':scope > .menu-list__submenu') ??
    element.parentElement?.querySelector('.menu-list__submenu, [ref="submenu[]"]');

  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * @param {HTMLElement | null | undefined} submenu
 * @param {boolean} [isMediaMenu]
 * @returns {number}
 */
function measureSubmenuHeight(submenu, isMediaMenu = false) {
  if (!submenu) return 0;

  const inner = submenu.querySelector('.menu-list__submenu-inner');
  const isOpen = submenu.dataset.active !== undefined;

  if (isMediaMenu || isOpen) {
    return Math.max(
      submenu.offsetHeight,
      submenu.scrollHeight,
      inner?.offsetHeight ?? 0,
      inner?.scrollHeight ?? 0
    );
  }

  const wasVisibility = submenu.style.visibility;
  submenu.style.visibility = 'visible';

  const measured = Math.max(
    submenu.offsetHeight,
    submenu.scrollHeight,
    inner?.offsetHeight ?? 0,
    inner?.scrollHeight ?? 0
  );

  if (wasVisibility) {
    submenu.style.visibility = wasVisibility;
  } else {
    submenu.style.removeProperty('visibility');
  }

  return measured > 0 ? measured : 0;
}

/**
 * @param {HTMLElement | null | undefined} item
 * @returns {string}
 */
function getMenuItemKey(item) {
  if (!(item instanceof HTMLElement)) return '';
  return item.getAttribute('aria-controls') ?? item.getAttribute('href') ?? '';
}

/**
 * @param {HeaderMenu} menu
 * @param {string} key
 * @returns {HTMLElement | null}
 */
function findMenuItemByKey(menu, key) {
  if (!key) return null;

  const byControls = menu.querySelector(`[ref="menuitem"][aria-controls="${key}"]`);
  if (byControls instanceof HTMLElement) return byControls;

  const byHref = menu.querySelector(`[ref="menuitem"][href="${key}"]`);
  if (byHref instanceof HTMLElement) return byHref;

  return null;
}
